import type {
  Announcement,
  AnnouncementCategory,
  AnnouncementComment,
  BoostAction,
  CreateCommentPayload,
  EditCommentPayload,
  ReactionCounts,
  ReactionType,
} from "../components/announcement";

import { apiRequest } from "./client";

export interface AnnouncementListResponse {
  data: Announcement[];
  total: number;
}

export interface AnnouncementReactionsResponse {
  reaction_counts: ReactionCounts;
  user_reaction: ReactionType | null;
}

export function fetchAnnouncementList(params: {
  offset: number;
  limit: number;
  categories?: string[];
  plans?: string[];
  signal?: AbortSignal;
}): Promise<AnnouncementListResponse> {
  return apiRequest<AnnouncementListResponse>("/api/announcement/list", {
    query: {
      offset: params.offset,
      limit: params.limit,
      categories: params.categories,
      plans: params.plans,
    },
    signal: params.signal,
  });
}

export function fetchAnnouncementCategories(signal?: AbortSignal): Promise<
  AnnouncementCategory[]
> {
  return apiRequest<AnnouncementCategory[]>("/api/announcement/categories", {
    signal,
  });
}

export function fetchAnnouncementById(
  announcementId: number,
  signal?: AbortSignal,
): Promise<Announcement> {
  return apiRequest<Announcement>(
    `/api/announcement/${encodeURIComponent(String(announcementId))}`,
    { signal },
  );
}

export function fetchReactions(
  announcementId: number,
  signal?: AbortSignal,
): Promise<AnnouncementReactionsResponse> {
  return apiRequest<AnnouncementReactionsResponse>(
    `/api/announcement/${announcementId}/reactions`,
    { signal },
  );
}

export function upsertReaction(
  announcementId: number,
  reactionType: ReactionType,
): Promise<void> {
  return apiRequest<void>(`/api/announcement/${announcementId}/reaction`, {
    method: "PUT",
    body: { reaction_type: reactionType },
  });
}

export function deleteReaction(announcementId: number): Promise<void> {
  return apiRequest<void>(`/api/announcement/${announcementId}/reaction`, {
    method: "DELETE",
  });
}

export function fetchComments(
  announcementId: number,
  signal?: AbortSignal,
): Promise<AnnouncementComment[]> {
  return apiRequest<AnnouncementComment[]>(
    `/api/announcement/${announcementId}/comments`,
    { signal },
  );
}

export function createComment(
  announcementId: number,
  payload: CreateCommentPayload,
): Promise<AnnouncementComment> {
  const formData = new FormData();
  formData.append("content", payload.content);
  if (payload.parent_comment_id !== null) {
    formData.append("parent_comment_id", String(payload.parent_comment_id));
  }
  if (payload.display_name !== undefined) {
    formData.append("display_name", payload.display_name);
  }
  for (const file of payload.images) {
    formData.append("images", file);
  }
  return apiRequest<AnnouncementComment>(
    `/api/announcement/${announcementId}/comments`,
    {
      method: "POST",
      body: formData,
    },
  );
}

export function editComment(
  announcementId: number,
  commentId: number,
  payload: EditCommentPayload,
): Promise<AnnouncementComment> {
  const formData = new FormData();
  formData.append("content", payload.content);
  if (payload.display_name !== undefined) {
    formData.append("display_name", payload.display_name);
  }
  for (const id of payload.remove_attachment_ids) {
    formData.append("remove_attachment_ids", String(id));
  }
  for (const file of payload.images) {
    formData.append("images", file);
  }
  return apiRequest<AnnouncementComment>(
    `/api/announcement/${announcementId}/comments/${commentId}`,
    {
      method: "PATCH",
      body: formData,
    },
  );
}

export function deleteComment(
  announcementId: number,
  commentId: number,
): Promise<void> {
  return apiRequest<void>(
    `/api/announcement/${announcementId}/comments/${commentId}`,
    { method: "DELETE" },
  );
}

export function fetchActiveBoostedPost(
  signal?: AbortSignal,
): Promise<Announcement | null> {
  return apiRequest<Announcement | null>(
    "/api/announcement/boost/active",
    { signal },
  );
}

export function logBoostAction(
  announcementId: number,
  action: BoostAction,
  url?: string,
): Promise<void> {
  return apiRequest<void>(
    `/api/announcement/${announcementId}/boost/${action}`,
    {
      method: "POST",
      body: url ? { url } : {},
    },
  );
}
